"use client";

import { useAdmin } from "@/lib/admin/context";
import { resolveCatalogVariant } from "@/components/cms/registry/variants";
import { resolveContainer } from "@/components/cms/registry/containers";
import type { CatalogByPathMeta } from "@/lib/api/cms-catalog";
import type { FrontendUserSession } from "@/lib/api/session";
import type { CmsBackground } from "@/lib/api/cms-windows";
import { useLocale } from "@/lib/i18n/context";
import { getMessages } from "@/lib/i18n";
import styles from "./CatalogRenderer.module.css";

/**
 * CatalogRenderer — dispatcher renderowania content'u katalogu na publicznej
 * stronie (analog `WindowRenderer`, ale dla `c_*` komponentów).
 *
 * **Data flow:**
 *   1. `[...slug]/page.tsx` (server) fetcha `fetchCatalogByPath(..., { includeContent: true })`
 *   2. Response zawiera `data.response_catalog_data` + `meta`
 *   3. CatalogRenderer dostaje wszystko jako prop (NIE używa context — content
 *      jest per-page, single, nie streamowany jak windows)
 *   4. Dispatch przez `resolveCatalogVariant(componentSymbol)` (z
 *      `components/cms/registry/variants.tsx` — single source of truth).
 *   5. Variant SAM renderuje content + tytuł strony (`<h1>` z `title`) +
 *      ewentualne tło — po cutover 2026-05-27 brak osobnej warstwy
 *      `<CatalogContainer>` z centralnym h1/wrapperem.
 *
 * **Fallback messages** (visible w fallback box) — bliźniaki z `WindowRenderer`:
 *   - `meta.missing_class` lub `meta.missing_method` -> "Brak metody
 *     `getApiContent` w klasie C_*_CONTROLLER" + link do dokumentacji
 *   - `meta.fetch_error` -> "Komponent rzucił wyjątek" (np. legacy `IAPI::*`
 *     ref w komponencie nieprzemigrowanym na IRESTAPI)
 *   - Brak React renderera dla `componentSymbol` -> "React-CMS nie ma jeszcze
 *     komponentu dla X"
 *   - `meta.invalid_response` -> "getApiContent zwróciło broken response"
 *
 * **Background:** `imp_cms_catalog.config[background]` (resolved przez
 * `IAPI_catalogByPath` z `m_site_backgrounds`) przekazywany jako prop do
 * variant'a — variant aplikuje wg uznania (inline style na swoim `<article>`).
 */
export function CatalogRenderer({
  componentSymbol,
  title,
  subtitle,
  content,
  background,
  meta,
  userSession,
  containerSymbol,
}: CatalogRendererProps) {
  const { adminPanelUrl, activeTemplate } = useAdmin();

  return (
    <div
      className={styles.root}
      data-catalog-symbol={componentSymbol ?? "none"}
    >
      <RenderContent
        componentSymbol={componentSymbol}
        title={title}
        subtitle={subtitle ?? null}
        content={content}
        background={background ?? null}
        meta={meta}
        adminPanelUrl={adminPanelUrl}
        userSession={userSession ?? null}
        activeTemplate={activeTemplate}
        containerSymbol={containerSymbol ?? null}
      />
    </div>
  );
}

export type CatalogRendererProps = {
  /** `c_symbol` z `imp_cms_component.symbol` (np. `c_descriptive_page`).
      NULL gdy katalog nie ma przypisanego komponentu. */
  componentSymbol: string | null;
  /** `imp_cms_catalog.name` — przekazywane do variant'a (variant decyduje
      czy renderuje `<h1>` z tytułem). */
  title: string;
  /** `imp_cms_catalog.name_subtitle` — opcjonalny podtytuł renderowany pod
      h1 w kontenerach z nagłówkiem (CDefault). Variant przekazuje do kontenera;
      kontenery typu "pusty" (CEmpty) pole ignorują. */
  subtitle?: string | null;
  /** Payload z `C_*_CONTROLLER::getApiContent()`. */
  content: unknown;
  /** Tło katalogu z `imp_cms_catalog.config[background]` przez `m_site_backgrounds`.
      Backend `IAPI_catalogByPath` resolves do CSS-ready struct. */
  background?: CmsBackground | null;
  /** Meta flagi z backendu (missing_class/missing_method/invalid_response). */
  meta?: CatalogByPathMeta;
  /** SSR-resolved user session — przekazywane do rendererów które reagują
      na stan logowania (`c_user_accounts`). NULL gdy visitor. Inne renderery
      ignorują prop. */
  userSession?: FrontendUserSession | null;
  /** Symbol kontenera (wrapper HTML) z `IAPI_catalogByPath.data.container_symbol`.
      Backend lookup 2-warstwowy w SQL (V1 per-podstrona + V1 default per komponent).
      `resolveContainer` mapuje symbol na React Container component, przekazywany
      do dispatch lambda jako `ContainerOverride`. NULL = lambda używa hardcoded
      fallback'u (`CDefault` dla content katalogów, `CEmpty` dla user_accounts). */
  containerSymbol?: string | null;
};

/* ─── Content render + fallback handlers ─────────────────────────────── */
/* Dispatch przez `resolveCatalogVariant` z components/cms/registry/variants.tsx
   (single source of truth — dodawanie typu/variantu: zob. dokumentacja header
   pliku variants.tsx). */

function RenderContent({
  componentSymbol,
  title,
  subtitle,
  content,
  background,
  meta,
  adminPanelUrl,
  userSession,
  activeTemplate,
  containerSymbol,
}: {
  componentSymbol: string | null;
  title: string;
  subtitle: string | null;
  content: unknown;
  background: CmsBackground | null;
  meta?: CatalogByPathMeta;
  adminPanelUrl: string;
  userSession: FrontendUserSession | null;
  activeTemplate: string | null;
  containerSymbol: string | null;
}) {
  /* Case 0: katalog nie ma c_symbol w DB (admin nie wybrał komponentu). */
  if (!componentSymbol) {
    return (
      <FallbackMessage
        title="Katalog bez komponentu"
        message={`Podstrona "${title}" nie ma przypisanego komponentu CMS — pole imp_cms_catalog.id_domain_component jest puste.`}
        hint="Otwórz konfigurację podstrony w Imperial Administrator i wybierz typ komponentu (np. c_descriptive_page)."
        docsAnchor="rest-api"
        adminPanelUrl={adminPanelUrl}
      />
    );
  }

  /* Case 1: backend nie ma klasy controlera. */
  if (meta?.missing_class) {
    return (
      <FallbackMessage
        title="Brak kontrolera komponentu"
        message={
          meta.message ??
          `Klasa ${meta.class_name} nie istnieje w /component/${componentSymbol}/.`
        }
        hint={`Dodaj plik component/${componentSymbol}/${componentSymbol}.php z klasą ${meta.class_name} extends ICOMPONENT_CONTROLLER implements ICOMPONENT.`}
        docsAnchor="rest-api"
        adminPanelUrl={adminPanelUrl}
      />
    );
  }

  /* Case 2: backend ma controller, ale brak metody getApiContent. */
  if (meta?.missing_method) {
    return (
      <FallbackMessage
        title="Brak metody `getApiContent` w komponencie backend"
        message={
          meta.message ??
          `Klasa ${meta.class_name} nie ma metody getApiContent(). Visitor nie może pobrać content'u dla podstrony "${title}".`
        }
        hint={
          `Dodaj public function getApiContent(array $api, array $params): array ` +
          `w component/${componentSymbol}/${componentSymbol}.php. ` +
          `Wzorzec: c_descriptive_page.php (zwraca { id, title, metatags, paragraphs })`
        }
        docsAnchor="rest-api"
        adminPanelUrl={adminPanelUrl}
      />
    );
  }

  /* Case 2.5: getApiContent() rzuciło wyjątek/error. Backend `\Throwable`
     fail-soft (IAPI_catalogByPath) zwraca meta.fetch_error zamiast wywalać
     cały endpoint. Najczęściej: PHP `\Error: Class "IAPI" not found` w
     komponencie nieprzemigrowanym na IRESTAPI. */
  if (meta?.fetch_error) {
    return (
      <FallbackMessage
        title="Błąd komponentu backend"
        message={
          meta.message ??
          `${meta.class_name ?? "Komponent"} rzucił wyjątek: ${meta.fetch_error}`
        }
        hint={
          `Sprawdź getApiContent() w component/${componentSymbol}/${componentSymbol}.php. ` +
          `Jeśli używa legacy IAPI::response — przemigruj na clean return [...] (wzorzec: c_descriptive_page.php). ` +
          `Pełny stack trace w logach backendu (data/logs/).`
        }
        docsAnchor="rest-api"
        adminPanelUrl={adminPanelUrl}
      />
    );
  }

  /* Case 3: response invalid (getApiContent nie zwróciło tablicy). */
  if (meta?.invalid_response) {
    return (
      <FallbackMessage
        title="Niepoprawna odpowiedź z backend"
        message={
          meta.message ??
          `${meta.class_name}::getApiContent() nie zwróciło tablicy.`
        }
        hint="Sprawdź czy metoda używa `return [...]` (a nie `IAPI::response(...)` ani `echo`). Wzorzec: c_descriptive_page.php."
        docsAnchor="rest-api"
        adminPanelUrl={adminPanelUrl}
      />
    );
  }

  /* Case 4: backend OK, ale React-CMS nie ma renderera dla tego typu katalogu. */
  const Renderer = resolveCatalogVariant(componentSymbol, activeTemplate);
  if (!Renderer) {
    return (
      <FallbackMessage
        title="Brak renderera Imperial CMS"
        message={`Podstrona typu "${componentSymbol}" ma działającą metodę backend (${meta?.class_name ?? "??"}), ale Imperial CMS nie ma jeszcze komponentu do wyświetlenia.`}
        hint={
          `Dodaj komponent C${snakeToPascal(componentSymbol)} w ` +
          `components/catalogs/C${snakeToPascal(componentSymbol)}/C${snakeToPascal(componentSymbol)}.tsx ` +
          `+ zarejestruj w \`catalogVariants.defaults\` w components/cms/registry/variants.tsx. ` +
          `Wzorzec: components/catalogs/CDescriptivePage/.`
        }
        docsAnchor="rest-api"
        adminPanelUrl={adminPanelUrl}
      />
    );
  }

  /* Case 5 (happy path): render real content. Variant SAM renderuje h1 z `title`
     i ewentualnie aplikuje `background` jako inline style. `ContainerOverride`
     z DB (`container_symbol`) - resolveContainer mapuje symbol z
     `imp_cms_container.symbol` na React Container component (np. 'catalog_default'
     -> CDefault, 'catalog_empty' -> CEmpty). Null gdy backend nie ma
     `container_symbol` lub symbol nieznany - dispatch lambda używa hardcoded
     fallback'u (CDefault dla content / CEmpty dla user_accounts). */
  const ContainerOverride = resolveContainer(containerSymbol);
  return (
    <Renderer
      content={content}
      title={title}
      subtitle={subtitle}
      background={background}
      userSession={userSession}
      ContainerOverride={ContainerOverride}
    />
  );
}

/* ─── Fallback message component ─────────────────────────────────────── */

function FallbackMessage({
  title,
  message,
  hint,
  docsAnchor,
  adminPanelUrl,
}: {
  title: string;
  message: string;
  hint: string;
  docsAnchor: string;
  adminPanelUrl: string;
}) {
  const t = getMessages(useLocale());
  const docsUrl = `${adminPanelUrl}?admin_page=system_administration&sys=documentation&doc=react_cms#${docsAnchor}`;
  return (
    <div className={styles.fallback} role="status">
      <div className={styles.fallbackTitle}>{title}</div>
      <p className={styles.fallbackMessage}>{message}</p>
      <p className={styles.fallbackHint}>{hint}</p>
      <a
        href={docsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.fallbackDocsLink}
      >
        {t.common.readDocs}
      </a>
    </div>
  );
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

/** `c_descriptive_page` -> `DescriptivePage` — pomocnik dla auto-generated hint'ów. */
function snakeToPascal(snake: string): string {
  return snake
    .replace(/^c_/, "")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}
